import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Plus, Trash, ArrowUp, ArrowDown, ClipboardText } from '@phosphor-icons/react';

const ROW_TYPES = [
  { type: 'section', label: 'Section Heading' },
  { type: 'text_row', label: 'Text / Input Row' },
  { type: 'long_text_row', label: 'Multi-line Row' },
  { type: 'date_row', label: 'Date Row' },
  { type: 'yes_no_row', label: 'Yes / No Row' },
  { type: 'checkbox_row', label: 'Checkbox Row' },
  { type: 'dropdown_row', label: 'Dropdown Row' },
  { type: 'note', label: 'Note / Instructions' },
];

/* Parse pasted text into structured rows */
export function parseTextToRows(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const rows = [];
  for (const line of lines) {
    const id = `field_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const isSectionNumbered = /^\d+[\.\)\s]*[A-Z]/.test(line);
    const isSectionColon = line.endsWith(':') && line.length < 60 && !line.startsWith('-') && !line.startsWith('\u2022');
    const isSectionAllCaps = line === line.toUpperCase() && line.length > 3 && line.length < 60 && /[A-Z]/.test(line);
    if (isSectionNumbered || isSectionColon || isSectionAllCaps) { rows.push({ id, type: 'section', content: line.replace(/:$/, '') }); continue; }
    const checkboxMatch = line.match(/^[\-\u2022\*\u2610\u25A1\>\u25B8]\s*(.+)$/);
    const bracketCheck = line.match(/^\[[\s\]]*\]\s*(.+)$/);
    if (checkboxMatch || bracketCheck) { rows.push({ id, type: 'checkbox_row', label: (checkboxMatch ? checkboxMatch[1] : bracketCheck[1]).trim() }); continue; }
    const numberedItem = line.match(/^\d+[\.\)]\s*[\-\u2022]?\s*(.+)$/);
    if (numberedItem) { rows.push({ id, type: /^\d+[\.\)]\s*[\-\u2022]/.test(line) ? 'checkbox_row' : 'text_row', label: numberedItem[1].trim() }); continue; }
    rows.push({ id, type: 'text_row', label: line });
  }
  return rows;
}

export function EditorRow({ field, index, total, onChange, onRemove, onMove }) {
  const cellBorder = 'border border-gray-400';
  const [showTypeMenu, setShowTypeMenu] = useState(false);

  const switchableTypes = [
    { type: 'text_row', label: 'Text Input' }, { type: 'checkbox_row', label: 'Checkbox' },
    { type: 'yes_no_row', label: 'Yes / No' }, { type: 'date_row', label: 'Date' },
    { type: 'long_text_row', label: 'Multi-line' }, { type: 'dropdown_row', label: 'Dropdown' },
  ];
  const currentTypeLabel = switchableTypes.find(t => t.type === field.type)?.label || field.type;
  const canSwitchType = !['section', 'note'].includes(field.type);

  const actionBtns = (
    <div className="absolute -right-10 top-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
      {index > 0 && <button type="button" onClick={() => onMove(index, index - 1)} className="p-0.5 bg-white border rounded hover:bg-gray-100" title="Move up"><ArrowUp className="w-3.5 h-3.5 text-gray-500" /></button>}
      {index < total - 1 && <button type="button" onClick={() => onMove(index, index + 1)} className="p-0.5 bg-white border rounded hover:bg-gray-100" title="Move down"><ArrowDown className="w-3.5 h-3.5 text-gray-500" /></button>}
      <button type="button" onClick={() => onRemove(index)} className="p-0.5 bg-white border rounded hover:bg-red-50" title="Delete"><Trash className="w-3.5 h-3.5 text-red-500" /></button>
    </div>
  );

  const typeSwitcher = canSwitchType ? (
    <div className="relative inline-block">
      <button type="button" onClick={() => setShowTypeMenu(!showTypeMenu)} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500 hover:bg-gray-300 hover:text-gray-700 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap" title="Change row type" data-testid={`type-switch-${index}`}>{currentTypeLabel}</button>
      {showTypeMenu && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 py-1 min-w-[130px]">
          {switchableTypes.map(st => (
            <button key={st.type} type="button" onClick={() => { onChange({ ...field, type: st.type, ...(st.type === 'dropdown_row' && !field.options ? { options: '' } : {}) }); setShowTypeMenu(false); }}
              className={`block w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 ${st.type === field.type ? 'font-bold text-blue-600' : 'text-gray-700'}`}>{st.label}</button>
          ))}
        </div>
      )}
    </div>
  ) : null;

  if (field.type === 'section') return (
    <tr className="group relative" data-testid={`form-field-row-${index}`}>
      <td colSpan={2} className={`${cellBorder} bg-gray-200 px-4 py-3`}>
        <input value={field.content || ''} onChange={(e) => onChange({ ...field, content: e.target.value })} placeholder="Section heading (e.g. 1. Personal Details)" className="w-full bg-transparent font-bold text-base text-gray-800 uppercase tracking-wide placeholder:normal-case placeholder:font-normal focus:outline-none" data-testid={`field-section-input-${index}`} />
      </td>{actionBtns}
    </tr>
  );

  if (field.type === 'note') return (
    <tr className="group relative" data-testid={`form-field-row-${index}`}>
      <td colSpan={2} className={`${cellBorder} px-4 py-3`}>
        <textarea value={field.content || ''} onChange={(e) => onChange({ ...field, content: e.target.value })} placeholder="Type notes or instructions here..." className="w-full min-h-[50px] bg-transparent text-base text-gray-600 italic resize-y focus:outline-none" data-testid={`field-note-input-${index}`} />
      </td>{actionBtns}
    </tr>
  );

  if (field.type === 'checkbox_row') return (
    <tr className="group relative" data-testid={`form-field-row-${index}`}>
      <td className={`${cellBorder} bg-gray-50 px-4 py-3 w-[55%]`}><div className="flex items-center gap-2"><input value={field.label || ''} onChange={(e) => onChange({ ...field, label: e.target.value })} placeholder="Item label (e.g. Passport Copy)" className="w-full bg-transparent text-base text-gray-800 placeholder:text-gray-400 focus:outline-none" data-testid={`field-label-input-${index}`} />{typeSwitcher}</div></td>
      <td className={`${cellBorder} px-4 py-3 text-center`}><div className="w-5 h-5 border-2 border-gray-400 rounded mx-auto" /></td>{actionBtns}
    </tr>
  );

  if (field.type === 'yes_no_row') return (
    <tr className="group relative" data-testid={`form-field-row-${index}`}>
      <td className={`${cellBorder} bg-gray-50 px-4 py-3 w-[55%]`}><div className="flex items-center gap-2"><input value={field.label || ''} onChange={(e) => onChange({ ...field, label: e.target.value })} placeholder="Question (e.g. Are you VAT registered?)" className="w-full bg-transparent text-base text-gray-800 placeholder:text-gray-400 focus:outline-none" data-testid={`field-label-input-${index}`} />{typeSwitcher}</div></td>
      <td className={`${cellBorder} px-4 py-3 text-base text-gray-500`}><span className="flex items-center gap-4"><span className="flex items-center gap-1"><span className="inline-block w-4 h-4 border-2 border-gray-400 rounded-full" /> Yes</span><span className="flex items-center gap-1"><span className="inline-block w-4 h-4 border-2 border-gray-400 rounded-full" /> No</span><span className="flex items-center gap-1"><span className="inline-block w-4 h-4 border-2 border-gray-400 rounded-full" /> Attached</span><span className="flex items-center gap-1"><span className="inline-block w-4 h-4 border-2 border-gray-400 rounded-full" /> N/A</span></span></td>{actionBtns}
    </tr>
  );

  if (field.type === 'dropdown_row') return (
    <tr className="group relative" data-testid={`form-field-row-${index}`}>
      <td className={`${cellBorder} bg-gray-50 px-4 py-3 w-[55%]`}><div className="flex items-center gap-2"><input value={field.label || ''} onChange={(e) => onChange({ ...field, label: e.target.value })} placeholder="Question label" className="w-full bg-transparent text-base text-gray-800 placeholder:text-gray-400 focus:outline-none" data-testid={`field-label-input-${index}`} />{typeSwitcher}</div></td>
      <td className={`${cellBorder} px-4 py-3`}><input value={typeof field.options === 'string' ? field.options : (Array.isArray(field.options) ? field.options.join(', ') : '')} onChange={(e) => onChange({ ...field, options: e.target.value })} placeholder="Options (comma separated)" className="w-full bg-transparent text-base text-gray-500 placeholder:text-gray-400 focus:outline-none" data-testid={`field-dropdown-options-${index}`} /></td>{actionBtns}
    </tr>
  );

  const placeholders = { 'text_row': 'Client writes here...', 'long_text_row': 'Client writes here (multi-line)...', 'date_row': 'DD / MM / YYYY' };
  return (
    <tr className="group relative" data-testid={`form-field-row-${index}`}>
      <td className={`${cellBorder} bg-gray-50 px-4 py-3 w-[55%]`}><div className="flex items-center gap-2"><input value={field.label || ''} onChange={(e) => onChange({ ...field, label: e.target.value })} placeholder="Field label (e.g. Full Legal Name)" className="w-full bg-transparent text-base text-gray-800 placeholder:text-gray-400 focus:outline-none" data-testid={`field-label-input-${index}`} />{typeSwitcher}</div></td>
      <td className={`${cellBorder} px-4 py-3 text-base text-gray-400 italic`}>{placeholders[field.type] || 'Client writes here...'}</td>{actionBtns}
    </tr>
  );
}

export function FormRowToolbar({ pasteMode, setPasteMode, pasteText, setPasteText, onPasteImport, onAddRow }) {
  if (pasteMode) return (
    <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 space-y-3" data-testid="paste-mode-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-blue-800">Paste your document content below</p>
          <p className="text-xs text-blue-600 mt-0.5">Each line becomes a row. Lines with numbers become section headings. Lines starting with - or &bull; become checkboxes.</p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={() => { setPasteMode(false); setPasteText(''); }}>Cancel</Button>
      </div>
      <textarea value={pasteText} onChange={(e) => setPasteText(e.target.value)} placeholder={"Paste your content here, e.g.:\n\n1. Personal Details\n- Unique Taxpayer Reference (UTR)\n- National Insurance Number"} className="w-full min-h-[200px] rounded-md border border-blue-300 bg-white px-4 py-3 text-base resize-y focus:outline-none focus:border-blue-500" data-testid="paste-textarea" autoFocus />
      <div className="flex items-center justify-between">
        <p className="text-xs text-blue-600">{pasteText.trim() ? `${pasteText.split('\n').filter(l => l.trim()).length} lines detected` : 'Waiting for content...'}</p>
        <Button type="button" onClick={onPasteImport} disabled={!pasteText.trim()} data-testid="convert-paste-btn">Convert to Table Rows</Button>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-3">
      <div className="flex items-center gap-3">
        <Button type="button" variant="default" className="text-sm h-10 px-4" onClick={() => setPasteMode(true)} data-testid="paste-content-btn">
          <ClipboardText className="w-4 h-4 mr-2" /> Paste Content (Auto-Detect Rows)
        </Button>
        <span className="text-sm text-gray-400">or add rows manually:</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {ROW_TYPES.map((rt) => (
          <Button key={rt.type} type="button" variant="outline" size="sm" className="text-sm h-9 px-3" onClick={() => onAddRow(rt.type)} data-testid={`add-row-${rt.type}`}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> {rt.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export { ROW_TYPES };
